import { useState, useCallback, useEffect } from 'react';
import { Modal } from 'antd';
import dayjs from 'dayjs';

import { useIntl } from 'react-intl';

import { installApp, manageApp } from 'services/app-store';
import { getMetaCatalog } from 'services/products';

import { META_CATALOG_NAME } from 'constants/pixels';
import { sendSegment } from 'utils/segment';
import { PRODUCTION_SLLR_BASE_API_URL } from 'constants/urls';

import { notify } from 'components/Notify/Notify';
import BRButton from 'components/BRButton/BRButton';
import ConnectionFirstStep from './components/ConnectionFirstStep/ConnectionFirstStep';
import ConnectionSecondStep from './components/ConnectionSecondStep/ConnectionSecondStep';

import { ReactComponent as Close } from 'assets/bosta-icons/Close.svg';

import './AddEditPixelIDModal.less';

const AddEditPixelIDModal = ({
  subtitle,
  modalSubtitle,
  apiKey,
  objKey,
  businessInfo,
  setBusinessInfo,
  isActivated,
  name,
  close,
  integratedPixelData,
  ...props
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;

  const [step, setStep] = useState(0);
  const [pixelValue, setPixelValue] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({ isVisible: false });
  const [metaCatalogData, setMetaCatalogData] = useState({});

  const onMetaCatalogSave = async () => {
    setIsLoading(true);
    try {
      if (!!step) {
        close();
      } else {
        const { username, password } = await getMetaCatalog();
        setMetaCatalogData({ ...metaCatalogData, username, password });
        sendSegment('meta_catalogue_save');
        setStep((s) => s + 1);
      }
    } catch (error) {
      notify({ msg: error.message, ...error });
    }
    setIsLoading(false);
  };

  const onSavePixelId = async () => {
    const cleanedPixelValue = pixelValue.replace(/\s/g, '');
    if (!cleanedPixelValue?.length && !isActivated) {
      setErrors({
        isVisible: true,
        text: formatMessage({ id: 'pixels.errors.field_is_required' })
      });
      return;
    } else if (
      pixelValue.includes('script') ||
      pixelValue.includes('function')
    ) {
      setErrors({
        isVisible: true,
        text: formatMessage({ id: 'pixels.errors.text_with_script' })
      });
      return;
    }
    setErrors({
      isVisible: false
    });
    setIsLoading(true);
    try {
      const payload = cleanedPixelValue?.length
        ? {
            plugins: {
              pixels: {
                [objKey]: {
                  [apiKey]: pixelValue,
                  ...(!integratedPixelData?.createdAt && {
                    createdAt: dayjs().format()
                  })
                }
              }
            }
          }
        : {
            unset: {
              plugins: { pixels: { [objKey]: 1 } }
            }
          };
      await (cleanedPixelValue?.length ? installApp : manageApp)(payload);

      setBusinessInfo((prev) => {
        return {
          ...prev,
          featuresConfigurations: {
            ...prev?.featuresConfigurations,
            plugins: {
              ...prev?.featuresConfigurations?.plugins,
              pixels: {
                ...prev?.featuresConfigurations?.plugins?.pixels,

                [objKey]: cleanedPixelValue.length
                  ? {
                      [apiKey]: pixelValue
                    }
                  : undefined
              }
            }
          }
        };
      });
      close();
      notify({
        msg: formatMessage({
          id: `pixels.notify.${
            cleanedPixelValue?.length ? 'added' : 'removed'
          }.${name}`
        }),
        type: 'success'
      });
    } catch (error) {
      notify({ msg: error.message, ...error });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    setPixelValue(isActivated);
  }, [isActivated]);

  useEffect(() => {
    setMetaCatalogData({
      url: `${PRODUCTION_SLLR_BASE_API_URL}products/catalog/${businessInfo?._id}`
    });
  }, [businessInfo]);

  const handleModalAction = useCallback(() => {
    if (!step) {
      name === META_CATALOG_NAME ? onMetaCatalogSave() : setStep((s) => s + 1);
    } else {
      name === META_CATALOG_NAME ? onMetaCatalogSave() : onSavePixelId();
    }
  }, [onSavePixelId, step, onMetaCatalogSave, name]);

  const RenderModalFooter = (
    <>
      <BRButton
        label={formatMessage({ id: 'common.cancel' })}
        type="outline"
        onClick={close}
      />
      <BRButton
        label={formatMessage({ id: `common.${!step ? 'continue' : 'save'}` })}
        onClick={handleModalAction}
        loading={isLoading}
        type="primary"
      />
    </>
  );

  return (
    <Modal
      {...props}
      footer={RenderModalFooter}
      onCancel={close}
      maskClosable={false}
      width={null}
      closeIcon={<Close />}
      wrapClassName="br-add-edit-pixel-id-modal"
      title={
        <div className="br-add-edit-pixel-id-modal__header">
          <span className="display-xs">
            {`${formatMessage({ id: 'pixels.install' })} ${formatMessage({
              id: `pixels.${[name]}.title`
            })}`}
          </span>
          {!step && (
            <span className="body text-gray">{modalSubtitle || subtitle}</span>
          )}
        </div>
      }
    >
      {!step ? (
        <ConnectionFirstStep intl={intl} name={name} {...props} />
      ) : (
        <ConnectionSecondStep
          data={{
            pixelValue,
            setPixelValue,
            setErrors,
            errors,
            apiKey,
            objKey,
            setBusinessInfo,
            isActivated,
            intl,
            name,
            metaCatalogData,
            isLoading,
            ...props
          }}
        />
      )}
    </Modal>
  );
};

export default AddEditPixelIDModal;
